<template>
  <main class="w-full">
    <header class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-3xl font-bold text-gray-800">Commandes</h1>
      <p class="text-gray-600 mb-2">Suivez et gérez les commandes clients</p>
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <icon name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            placeholder="Rechercher par ID, client ou email..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :value="searchTerm"
            @input="searchTerm = $event.target.value"
          />
        </div>
        <select
          class="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :value="filterStatus"
          @change="filterStatus = $event.target.value"
        >
          <option value="">Tous les statuts</option>
          <option value="1">En cours</option>
          <option value="2">Accepté</option>
          <option value="3">Expédié</option>
          <option value="4">Livré</option>
          <option value="0">Rejeté</option>
        </select>
        
        <!-- Nouveau filtre pour les statuts de paiement -->
        <select
          class="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          :value="filterPaymentStatus"
          @change="filterPaymentStatus = $event.target.value"
        >
          <option value="">Tous les paiements</option>
          <option value="1">En attente</option>
          <option value="2">Payé</option>
          <option value="3">Échoué</option>
        </select>
      </div>
    </header>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-700">Erreur lors du chargement des commandes: {{ error }}</p>
        <button 
          @click="fetchOrders"
          class="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Réessayer
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!filteredOrders.length" class="text-center py-12">
        <icon name="Package" :size="48" class="mx-auto text-gray-400 mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">Aucune commande</h3>
        <p class="text-gray-600 mb-4">Aucune commande ne correspond à vos critères</p>
        <button 
          v-if="searchTerm || filterStatus || filterPaymentStatus"
          @click="clearFilters"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Réinitialiser les filtres
        </button>
      </div>

      <!-- Orders Table -->
      <div v-else class="bg-white rounded-xl shadow w-full overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">N° Commande</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Client</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Date</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Articles</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Total</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Statut</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Paiement</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="order in paginatedOrders" 
                :key="order.id"
                class="border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <td class="p-4 font-medium text-gray-900">
                  <span class="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">
                    {{ order.order_number }}
                  </span>
                </td>
                <td class="p-4">
                  <div>
                    <p class="font-medium text-gray-900">{{ order.customer_name }}</p>
                    <p class="text-xs text-gray-500">{{ order.customer_phone }}</p>
                    <p class="text-xs text-gray-500">{{ order.customer_email || order.user?.email }}</p>
                  </div>
                </td>
                <td class="p-4 text-sm text-gray-700">
                  {{ formatDate(order.created_at) }}
                </td>
                <td class="p-4 text-sm text-gray-700">
                  {{ order.items_count || order.order_items?.length || 0 }} article(s)
                </td>
                <td class="p-4 font-medium text-gray-900">
                  {{ formatPrice(order.total) }}
                </td>
                <td class="p-4">
                  <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`">
                    {{ getStatusLabel(order.status) }}
                  </span>
                </td>
                <td class="p-4">
                  <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`">
                    {{ getPaymentStatusLabel(order.payment_status) }}
                  </span>
                </td>
                <td class="p-4">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="selectOrder(order)"
                      class="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
                    >
                      <icon name="Eye" :size="16" class="mr-1" /> Détails
                    </button>
                    
                   <!-- Sélecteur de statut de commande -->
<select
  v-if="order.status !== 0 && order.status !== 4"  
  :value="order.status"
  @change="updateOrderStatus(order.id, $event.target.value)"
  :disabled="updatingStatus === order.id"
  class="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
>
  <option value="1">En cours</option>
  <option value="2">Accepté</option>
  <option value="3">Expédié</option>
  <option value="4">Livré</option>
  <option value="0">Rejeter</option>
</select>

<!-- Sélecteur de statut de paiement -->
<select
  :value="order.payment_status"
  @change="updatePaymentStatus(order.id, $event.target.value)"
  :disabled="updatingPaymentStatus === order.id"
  class="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
>
  <option value="1">En attente</option>
  <option value="2">Payé</option>
  <option value="3">Échoué</option>
</select>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Pagination -->
        <div v-if="totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div class="text-sm text-gray-700">
            Page {{ currentPage }} sur {{ totalPages }}
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            
            <div class="flex items-center space-x-1">
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-3 py-1 rounded text-sm',
                  currentPage === page 
                    ? 'bg-blue-600 text-white' 
                    : 'border border-gray-300 hover:bg-gray-50'
                ]"
              >
                {{ page }}
              </button>
              <span v-if="showEllipsis" class="px-2 text-gray-500">...</span>
            </div>
            
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
          
          <div class="flex items-center space-x-2">
            <span class="text-sm text-gray-700">Afficher</span>
            <select
              v-model="itemsPerPage"
              @change="resetPagination"
              class="px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Detail Modal -->
    <OrderDetailModal
      v-if="selectedOrder"
      :order="selectedOrder"
      @close="selectedOrder = null"
      @update-status="handleStatusUpdate"
    />
  </main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import OrderDetailModal from './OrderDetailModal.vue'

export default {
  name: 'OrdersPage',
  
  components: {
    OrderDetailModal
  },
  
  data() {
    return {
      searchTerm: '',
      filterStatus: '',
      filterPaymentStatus: '',
      selectedOrder: null,
      updatingStatus: null,
      updatingPaymentStatus: null,
      
      // Pagination
      currentPage: 1,
      itemsPerPage: 10,
      
      // Local state for admin orders
      adminOrders: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        has_more: false
      }
    }
  },
  
  computed: {
    ...mapState(['admin', 'loading', 'error']),
    
    orders() {
      return this.adminOrders.length ? this.adminOrders : this.admin.orders
    },
    
    filteredOrders() {
      return this.orders.filter(order => {
        // Recherche
        const searchLower = this.searchTerm.toLowerCase()
        const matchesSearch = 
          (order.order_number && order.order_number.toLowerCase().includes(searchLower)) ||
          (order.customer_name && order.customer_name.toLowerCase().includes(searchLower)) ||
          (order.customer_email && order.customer_email.toLowerCase().includes(searchLower)) ||
          (order.customer_phone && order.customer_phone.includes(searchLower)) ||
          (order.user?.email && order.user.email.toLowerCase().includes(searchLower)) ||
          (order.user?.name && order.user.name.toLowerCase().includes(searchLower))
        
        // Filtre statut
        const matchesStatus = !this.filterStatus || order.status.toString() === this.filterStatus
        
        // Filtre statut paiement
        const matchesPaymentStatus = !this.filterPaymentStatus || order.payment_status.toString() === this.filterPaymentStatus
        
        return matchesSearch && matchesStatus && matchesPaymentStatus
      })
    },
    
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredOrders.slice(start, end)
    },
    
    totalPages() {
      return Math.ceil(this.filteredOrders.length / this.itemsPerPage)
    },
    
    visiblePages() {
      const pages = []
      const maxVisible = 5
      
      if (this.totalPages <= maxVisible) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i)
        }
      } else {
        let start = Math.max(1, this.currentPage - 2)
        let end = Math.min(this.totalPages, start + maxVisible - 1)
        
        if (end - start + 1 < maxVisible) {
          start = Math.max(1, end - maxVisible + 1)
        }
        
        for (let i = start; i <= end; i++) {
          pages.push(i)
        }
      }
      
      return pages
    },
    
    showEllipsis() {
      return this.totalPages > 5 && this.currentPage < this.totalPages - 2
    }
  },
  
  async created() {
    await this.fetchAdminOrders()
  },
  
  methods: {
    ...mapActions(['fetchAdminOrders', 'updateOrderStatus']),
    
    async fetchAdminOrders(page = 1) {
      try {
        const result = await this.$store.dispatch('fetchOrders')
        if (result) {
          this.adminOrders = result.orders
          this.pagination = {
            current_page: result.current_page,
            last_page: result.last_page,
            total: result.total,
            has_more: result.has_more
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error)
      }
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF'
      }).format(price)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    
    formatDateFull(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getStatusColor(status) {
      switch (parseInt(status)) {
        case 4: return 'bg-green-100 text-green-800' // Livré
        case 3: return 'bg-blue-100 text-blue-800' // Expédié
        case 2: return 'bg-purple-100 text-purple-800' // Accepté
        case 1: return 'bg-yellow-100 text-yellow-800' // En cours
        case 0: return 'bg-red-100 text-red-800' // Rejeté
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        0: 'Rejeté',
        1: 'En cours',
        2: 'Accepté',
        3: 'Expédié',
        4: 'Livré'
      }
      return labels[statusNum] || `Statut ${status}`
    },
    
    getPaymentStatusColor(status) {
      switch (parseInt(status)) {
        case 2: return 'bg-green-100 text-green-800' // Payé
        case 1: return 'bg-yellow-100 text-yellow-800' // En attente
        case 3: return 'bg-red-100 text-red-800' // Échoué
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getPaymentStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        1: 'En attente',
        2: 'Payé',
        3: 'Échoué'
      }
      return labels[statusNum] || `Paiement ${status}`
    },
    
    // Dans votre composant, remplacez les méthodes :
async updateOrderStatus(orderId, newStatus) {
  this.updatingStatus = orderId
  try {
    // Utiliser l'action du store
    await this.$store.dispatch('updateOrderStatus', {
      id: orderId,
      status: parseInt(newStatus)
    })
    
    // this.$toast.success('Statut mis à jour avec succès')
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut:', error)
    // this.$toast.error('Erreur lors de la mise à jour du statut: ' + (error.message || 'Erreur inconnue'))
  } finally {
    this.updatingStatus = null
  }
},

async updatePaymentStatus(orderId, newStatus) {
  this.updatingPaymentStatus = orderId
  try {
    // Utiliser l'action du store
    await this.$store.dispatch('updateOrderPaymentStatus', {
      id: orderId,
      payment_status: parseInt(newStatus)
    })
    
    // this.$toast.success('Statut de paiement mis à jour avec succès')
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut de paiement:', error)
    // this.$toast.error('Erreur lors de la mise à jour du statut de paiement: ' + (error.message || 'Erreur inconnue'))
  } finally {
    this.updatingPaymentStatus = null
  }
},
    
    selectOrder(order) {
      this.selectedOrder = order
    },
    
    handleStatusUpdate({ orderId, newStatus }) {
      this.updateOrderStatus(orderId, newStatus)
    },
    
    clearFilters() {
      this.searchTerm = ''
      this.filterStatus = ''
      this.filterPaymentStatus = ''
      this.currentPage = 1
    },
    
    // Méthodes de pagination
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    
    goToPage(page) {
      this.currentPage = page
    },
    
    resetPagination() {
      this.currentPage = 1
    }
  }
}
</script>

<style scoped>
/* Styles spécifiques au composant */
input, select {
  transition: all 0.2s ease;
}

input:focus, select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Animation pour la modal */
.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>